
import { claimsOf, scopeRootOfStoredToken, subjectOfClaims } from '../util/jwt';
import { isSwitching } from './switch-in-progress';
import { getEntityScope } from './entity-scope-read';
import { confirmedMoveTo } from './scope-move-tracker';
import { track } from './telemetry';
import { rootMovedSince, UNREACHABLE_ROOT, userChangedSince } from '../slice/entityScopeSlice';

/**
 * The per-request entity-scope header (DV-3013 spec §3.9 / D1 / BC-7).
 *
 * `RequestContextInterceptor.SELECTED_ENTITY_HEADER` in commons-security; its
 * `Outcome.NO_SELECTION` ("no X-Entity-UUID header — scoped by the token as
 * today. Allowed.") is what the UI produced before this and what it still
 * produces for an unscoped session.
 *
 * Checked by `EntityScopeGuardInterceptor` in every service. When written
 * (spec §2.5) the guard shipped `Mode.OFF`; as of 13 Oct 2026 QA enforces it
 * with `honour-selection` on, and prod runs it log-only with that off.
 *
 * Sourced from `getEntityScope()` — the same accessor every other consumer
 * uses — so the header can never name an entity the UI is not showing. That is
 * the property DV-3006 did not have.
 *
 * It is left off when the request's own bearer token belongs to a DIFFERENT
 * USER than the one this tab's scope was written under (`userChangedSince`,
 * DV-3573; DV-3421 originally compared the token's `EntityUUID` claim, which
 * stopped meaning anything once a switch stopped moving the token). That tab
 * belongs to a session that is gone, and `reconcileScope()` reloads it. A tab
 * that deliberately chose an account its token does not name is the normal
 * case now and sends. A suppression that outlives the reload is reported, not
 * silently dropped — see `noteSuppression` below.
 *
 * It is NOT redirected during this tab's own cross-tree switch
 * (`switch-in-progress.ts`): until the commit the UI shows the account being
 * left, so that is what every request asserts, and the moved token's 403 is
 * the safe failure for anything the old page still fires.
 */
export const ENTITY_SCOPE_HEADER = 'X-Entity-UUID';

/**
 * The entity to assert, or null to leave the request token-scoped.
 *
 * <p>Suppressed when `userChangedSince` says the token has changed hands — see
 * the module docstring. DV-3573 review finding 3 explains why this keys on
 * `sub` rather than the entity claim: once a switch stops refreshing the
 * token, the claim only moves for a login or an old-bundle tab switching the
 * old way during the deploy window, and treating THAT as staleness left the
 * tab silently token-scoped forever. The backend checks every header now and
 * answers a visible 403 for one the caller may not use.
 */
export const entityScopeHeaderValue = (token: string | null | undefined): string | null => {
  // Both read off THIS request's token, never the stored one: the header has to
  // be right for the token it travels with, and the stored one may have been
  // replaced since this one was attached. Parsed once — this runs on every
  // request (review round 5, item 9).
  const claims = claimsOf(token);
  const sub = subjectOfClaims(claims);

  // DV-3421: a cross-tree switch in THIS tab gets no say either. Once its
  // refresh has moved the token, the slice below still names the account
  // being left, and that is what goes out — the old account's page is still
  // mounted until `commitSwitch`, and a create it fires in that window takes
  // its owner from the selection (review of b5251a34, item 2). Under the
  // moved token the guard refuses the old selection with a 403, which
  // `refusedForKnownUnreachableSelection` surfaces as is. The switch's own
  // calls are `tokenOnly` and never reach here.
  const scope = getEntityScope();
  if (scope.status !== 'scoped') return null;

  if (sub !== null && userChangedSince(sub, scope)) {
    noteSuppression(sub, scope.entityId);
    return null;
  }

  // NOT suppressed when the token's root has moved off this tab's stamp — a
  // cross-tree switch in another tab, or an admin adding the user to another
  // company (DV-3421 review round 5, item 1). Leaving the header off would
  // scope the request by the TOKEN, i.e. the other tree's account, under a
  // screen still naming this one: reads from the wrong account, and an order,
  // upload or caption edit landing in it. Sending the selection gets a 403
  // instead, which is the safe failure, and `reconcileScope` — run on the
  // `storage` event, on this tab's own token writes, and on focus — reloads
  // the tab onto the new account.
  mismatch = null;
  return scope.entityId;
};

/**
 * How long a suppression may last before it stops looking like the reload
 * window. When another user's login lands in shared storage, this tab's
 * `storage` listener runs `reconcileScope()`, which reloads it — but requests
 * already in flight, or fired before the listener runs, are suppressed in
 * between. Sub-second in practice; a suppression still going after this long
 * means the reload never happened.
 */
export const SUPPRESSION_GRACE_MS = 10_000;

let mismatch: {
  tokenSub: string;
  selected: string | null;
  since: number;
  reported: boolean;
} | null = null;

/**
 * Keeps the suppression visible. Without it a tab that never reloads after
 * another user took over the token — no `storage` event reached it, and it was
 * never focused — would read as plain `NO_SELECTION` forever: DV-3065's
 * measurement could not tell it apart, and once `honour-selection` is on the
 * UI would show one user's account while the data is scoped to another's.
 *
 * <p>The event's `tokenSub` field is a USER id. Events recorded before DV-3573
 * carried an entity id in a `claim` field instead; the rename keeps the two
 * populations filterable apart.
 *
 * Every suppression is a `console.debug` (hidden by default, but in the
 * LogRocket console feed). A mismatch still present after
 * `SUPPRESSION_GRACE_MS` is tracked once per user/slice pair as a LogRocket
 * Custom Event, so persistent desyncs are filterable apart from the window.
 */
const noteSuppression = (tokenSub: string, selected: string | null): void => {
  try {
    const now = Date.now();
    if (!mismatch || mismatch.tokenSub !== tokenSub || mismatch.selected !== selected) {
      mismatch = { tokenSub, selected, since: now, reported: false };
    }
    console.debug(
      `[${ENTITY_SCOPE_HEADER}] suppressed: token belongs to user ${tokenSub}, not the user this tab's scope (${selected}) was written under`,
    );

    const ageMs = now - mismatch.since;
    if (!mismatch.reported && ageMs >= SUPPRESSION_GRACE_MS) {
      mismatch.reported = true;
      // `reason` is kept for continuity with events recorded while round 4's
      // root-moved suppression existed; it is the only reason left.
      track('entity-scope-header-suppressed', {
        reason: 'user-changed',
        tokenSub,
        selected: selected ?? '',
        ageMs,
      });
    }
  } catch {
    // Diagnostics must never take the request with them.
  }
};

/** Test seam: forget any mismatch being tracked. */
export const resetSuppressionTracking = (): void => {
  mismatch = null;
};

/**
 * A 403 for a request that carried this tab's selection, while that selection
 * is KNOWN to be out of the token's reach (DV-3421, review of 291019f0, item
 * 6). The refusal is the expected answer — the tab is waiting to reload,
 * possibly for as long as unsaved work is open — and a refresh would not
 * change it: it would only cost a Keycloak round trip per poll and write a
 * token that fires `storage` in every other tab. So it is surfaced as is.
 *
 * <p>"Known" means the backend said so (an UNREACHABLE stamp), the reconciler
 * has CONFIRMED the move (review of 24a2f326, item 2), or this tab's own
 * cross-tree switch is mid-flight (review of b5251a34, item 2): it moved the
 * token itself, and the selection it keeps sending until the commit is the
 * one being left. A root that merely differs from the stamp may be a stale
 * token still being verified, and there a refresh-and-retry is exactly what
 * can recover the request.
 */
export const refusedForKnownUnreachableSelection = (
  sentSelection: string | null | undefined,
): boolean => {
  if (!sentSelection) return false;
  if (isSwitching()) return true;
  const scope = getEntityScope();
  if (scope.rootAtWrite === UNREACHABLE_ROOT) return true;
  const moved = confirmedMoveTo();
  return moved !== null && moved === scopeRootOfStoredToken() && rootMovedSince(moved, scope);
};
